import './shared-nav';
import type { BlogPost } from './blog-types';
import { blogAPI } from './blog-api';
import { creditService, type CreditInfo } from './credit-service';
import { showWarning } from './modal';
import { API_BASE_URL } from './config';

/**
 * Blog List Page Controller
 * Handles the blog listing page (blog.html)
 */
class BlogListUI {
  private currentPage: number = 1;
  private isLoading: boolean = false;
  private totalPages: number = 1;
  private editMode: boolean = false;
  private userWalletAddress: string | null = null;
  private userCredits: number = 0;
  private currentTab: 'all' | 'my' = 'all';
  private allPosts: BlogPost[] = [];

  /**
   * Initialize the blog list page
   */
  async initialize(): Promise<void> {
    await this.checkUserAuth();
    await this.loadBlogPosts();
    this.setupEventListeners();
    this.updateCreditDisplay();
  }

  /**
   * Check if user is authenticated and load credits
   */
  private async checkUserAuth(): Promise<void> {
    // Check if user is connected (from XamanAuth)
    const walletAddress = localStorage.getItem('xaman_wallet_address');

    console.log('🔍 Checking wallet connection:', walletAddress);

    if (walletAddress) {
      this.userWalletAddress = walletAddress;
      await this.loadUserCredits();
    } else {
      console.log('❌ No wallet connected');
    }
  }

  /**
   * Load user credits and update display
   */
  private async loadUserCredits(): Promise<void> {
    if (!this.userWalletAddress) return;

    try {
      console.log('📍 Wallet Address:', this.userWalletAddress);

      // Fetch AOX balance from the backend API
      const balanceResponse = await fetch(`${API_BASE_URL}/xaman/balance/${this.userWalletAddress}`);
      const balanceData = await balanceResponse.json();

      let aoxBalance = 0;
      if (balanceData.success) {
        aoxBalance = parseFloat(balanceData.balance) || 0;
        console.log('💰 AOX Balance from API:', aoxBalance);

        // Store in localStorage for future reference
        localStorage.setItem(`aox_balance_${this.userWalletAddress}`, balanceData.balance);
      } else {
        console.error('❌ Failed to fetch balance:', balanceData);
        // Try to get from localStorage as fallback
        const cachedBalance = localStorage.getItem(`aox_balance_${this.userWalletAddress}`);
        if (cachedBalance) {
          aoxBalance = parseFloat(cachedBalance);
          console.log('💰 Using cached balance:', aoxBalance);
        }
      }

      // Update credits based on AOX balance
      const creditInfo = await creditService.updateCredits(this.userWalletAddress, aoxBalance);

      console.log('💳 Credit Info:', creditInfo);

      if (creditInfo.success) {
        this.userCredits = creditInfo.credits;
        console.log('✅ Credits loaded:', this.userCredits);
        this.updateCreditDisplay();
      } else {
        console.error('❌ Failed to load credits:', creditInfo);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    }
  }

  /**
   * Update credit display in UI
   */
  private updateCreditDisplay(): void {
    const generateBtn = document.getElementById('generateBlogBtn') as HTMLButtonElement;
    const editBtn = document.getElementById('editModeBtn') as HTMLButtonElement;

    if (!generateBtn) return;

    if (!this.userWalletAddress) {
      // Not connected - disable generate and hide edit
      generateBtn.textContent = '🔒 Connect Wallet to Generate';
      generateBtn.disabled = true;
      generateBtn.title = 'Please connect your wallet to generate blog posts';

      if (editBtn) {
        editBtn.style.display = 'none';
      }
    } else {
      // Connected - show credit count and enable edit
      generateBtn.textContent = `✨ New Post (${this.userCredits} credits)`;
      generateBtn.disabled = this.userCredits < 1;
      generateBtn.title = this.userCredits < 1
        ? 'You need at least 1 credit to generate a blog post. Hold 1,000,000 AOX to earn 1 credit per day.'
        : 'Generate a new blog post (costs 1 credit)';

      if (editBtn) {
        editBtn.style.display = 'block';
      }
    }
  }

  /**
   * Load and display blog posts
   */
  async loadBlogPosts(page: number = 1): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();
    this.updatePaginationButtons(true);

    try {
      const response = await blogAPI.getBlogPosts(page, 100); // Load more to filter client-side

      if (response.success && response.posts) {
        this.allPosts = response.posts;
        this.filterAndRenderPosts();
      } else {
        this.showError(response.message || 'Failed to load blog posts');
      }
    } catch (error) {
      console.error('Error loading blog posts:', error);
      this.showError('Cosmic interference detected!');
    } finally {
      this.isLoading = false;
      this.updatePaginationButtons(false);
    }
  }

  /**
   * Filter posts based on current tab and render
   */
  private filterAndRenderPosts(): void {
    let postsToShow: BlogPost[] = [];

    if (this.currentTab === 'all') {
      postsToShow = this.allPosts;
    } else if (this.currentTab === 'my') {
      // Filter only posts created by the connected wallet
      postsToShow = this.allPosts.filter(post => {
        console.log('🔍 Checking post:', {
          title: post.title,
          authorWallet: post.authorWallet,
          userWallet: this.userWalletAddress,
          match: post.authorWallet === this.userWalletAddress
        });
        return post.authorWallet && post.authorWallet === this.userWalletAddress;
      });
      console.log('✅ Filtered posts for My Articles:', postsToShow.length);
    }

    // Pagination
    const postsPerPage = 9;
    const startIndex = (this.currentPage - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    const paginatedPosts = postsToShow.slice(startIndex, endIndex);

    this.totalPages = Math.ceil(postsToShow.length / postsPerPage);
    this.renderBlogPosts(paginatedPosts);
    this.updatePaginationInfo();
  }

  /**
   * Render blog posts to the DOM
   */
  private renderBlogPosts(posts: BlogPost[]): void {
    const container = document.getElementById('blogPostsContainer');
    if (!container) return;

    if (posts.length === 0) {
      container.innerHTML = `
        <div class="no-posts">
          <p>No blog posts yet. Generate your first post to get started.</p>
          <button class="btn-primary" onclick="window.blogListUI.generateNewPost()">
            Generate First Post
          </button>
        </div>
      `;
      return;
    }

    container.innerHTML = posts.map(post => this.createPostCard(post)).join('');
  }

  /**
   * Create HTML for a single blog post card
   */
  private createPostCard(post: BlogPost): string {
    const publishedDate = new Date(post.publishedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Check if this post belongs to the connected user and we're in "My Articles" tab
    const isOwnPost = this.userWalletAddress && post.authorWallet === this.userWalletAddress;
    const showDeleteButton = this.currentTab === 'my' && isOwnPost;

    // Build author HTML - make clickable if authorWallet exists
    const authorHTML = post.authorWallet
      ? `<a href="profile.html?address=${post.authorWallet}" class="blog-author blog-author-clickable" onclick="event.stopPropagation()">👽 ${post.author}</a>`
      : `<span class="blog-author">👽 ${post.author}</span>`;

    return `
      <article class="blog-card" data-post-id="${post.id}" data-author-wallet="${post.authorWallet || ''}">
        ${showDeleteButton ? `<button class="blog-delete-btn blog-delete-btn-visible" onclick="window.blogListUI.deletePost('${post.id}', event)" title="Delete post">×</button>` : ''}
        <div onclick="window.location.href='blog-post.html?id=${post.id}'" style="cursor: pointer;">
          <div class="blog-card-image">
            <img src="${post.imageUrl}" alt="${post.title}" loading="lazy">
          </div>
          <div class="blog-card-content">
            <div class="blog-meta">
              ${authorHTML}
              <span class="blog-date">${publishedDate}</span>
              <span class="blog-read-time">${post.readTime} min read</span>
            </div>
            <h3 class="blog-title">${post.title}</h3>
            <p class="blog-excerpt">${post.excerpt}</p>
            <div class="blog-card-actions">
              <button class="blog-read-more">
                Read More →
              </button>
              ${post.source ? `<a href="${post.source}" target="_blank" class="blog-source" onclick="event.stopPropagation()">View Source</a>` : ''}
            </div>
          </div>
        </div>
      </article>
    `;
  }

  /**
   * Switch between tabs (All Articles / My Articles)
   */
  switchTab(tab: 'all' | 'my'): void {
    // Don't allow "My Articles" if not connected
    if (tab === 'my' && !this.userWalletAddress) {
      showWarning('Please connect your wallet to view your articles');
      return;
    }

    console.log('🔄 Switching to tab:', tab);
    console.log('👤 User wallet:', this.userWalletAddress);
    console.log('📦 Total posts:', this.allPosts.length);

    this.currentTab = tab;
    this.currentPage = 1; // Reset to first page

    // Update tab styling
    const tabs = document.querySelectorAll('.blog-tab');
    tabs.forEach(tabEl => {
      if (tabEl.getAttribute('data-tab') === tab) {
        tabEl.classList.add('active');
      } else {
        tabEl.classList.remove('active');
      }
    });

    // Re-filter and render posts
    this.filterAndRenderPosts();
  }

  /**
   * Show custom confirmation modal
   */
  private showConfirmModal(message: string, onConfirm: () => void): void {
    const modal = document.createElement('div');
    modal.className = 'custom-modal-overlay';
    modal.innerHTML = `
      <div class="custom-modal">
        <div class="custom-modal-content">
          <h3>Confirm Action</h3>
          <p>${message}</p>
          <div class="custom-modal-actions">
            <button class="modal-btn modal-btn-cancel" id="modalCancel">Cancel</button>
            <button class="modal-btn modal-btn-confirm" id="modalConfirm">Delete</button>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Trigger animation
    setTimeout(() => modal.classList.add('show'), 10);

    // Event listeners
    const cancelBtn = modal.querySelector('#modalCancel') as HTMLButtonElement;
    const confirmBtn = modal.querySelector('#modalConfirm') as HTMLButtonElement;

    const closeModal = () => {
      modal.classList.remove('show');
      setTimeout(() => modal.remove(), 300);
    };

    cancelBtn.addEventListener('click', closeModal);
    confirmBtn.addEventListener('click', () => {
      closeModal();
      onConfirm();
    });

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });
  }

  /**
   * Delete a blog post
   */
  async deletePost(postId: string, event: Event): Promise<void> {
    event.stopPropagation();

    // Check if user is connected
    if (!this.userWalletAddress) {
      this.showError('Please connect your wallet to delete posts');
      return;
    }

    this.showConfirmModal('Are you sure you want to delete this blog post? This action cannot be undone.', async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/blog/posts/${postId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            walletAddress: this.userWalletAddress
          })
        });

        const data = await response.json();

        if (data.success) {
          this.showSuccess('Post deleted successfully');
          await this.loadBlogPosts(this.currentPage);
        } else {
          this.showError(data.error || 'Failed to delete post');
        }
      } catch (error) {
        console.error('Error deleting post:', error);
        this.showError('Failed to delete post');
      }
    });
  }

  /**
   * Generate a new blog post
   */
  async generateNewPost(): Promise<void> {
    // Check if user is connected
    if (!this.userWalletAddress) {
      this.showError('Please connect your wallet to generate blog posts');
      return;
    }

    // Check if user has enough credits
    if (this.userCredits < 1) {
      this.showError('Insufficient credits! You need at least 1 credit to generate a blog post. Hold 1,000,000 AOX tokens to earn 1 credit per day.');
      return;
    }

    const generateBtn = document.getElementById('generateBlogBtn') as HTMLButtonElement;
    if (generateBtn) {
      generateBtn.disabled = true;
      generateBtn.textContent = 'Generating...';
    }

    this.showLoading('Spending 1 credit and generating blog post with AI...');

    try {
      // Spend credits first
      const spendResult = await creditService.spendCredits(this.userWalletAddress, 1);

      if (!spendResult.success) {
        this.showError(spendResult.error || 'Failed to spend credits');
        return;
      }

      // Update local credit count
      this.userCredits = spendResult.remainingCredits;
      this.updateCreditDisplay();

      // Generate the blog post with the user's wallet address
      const response = await blogAPI.autoGenerateBlogPost(this.userWalletAddress);

      if (response.success && response.post) {
        await this.loadBlogPosts(1);
        this.showSuccess(`Blog post generated successfully! You have ${this.userCredits} credits remaining.`);
      } else {
        this.showError(response.message || response.error || 'Generation failed');

        // Refund credit on failure (optional - you may want to keep this or remove it)
        // For now, we'll keep the credit spent even on failure to prevent abuse
      }
    } catch (error) {
      console.error('Error generating post:', error);
      this.showError('Failed to generate post. Try again!');
    } finally {
      if (generateBtn) {
        generateBtn.disabled = this.userCredits < 1;
        this.updateCreditDisplay();
      }
    }
  }

  /**
   * Update pagination info display
   */
  private updatePaginationInfo(): void {
    const info = document.getElementById('paginationInfo');
    if (info) {
      info.textContent = `Page ${this.currentPage} of ${this.totalPages}`;
    }
  }

  /**
   * Update pagination buttons state
   */
  private updatePaginationButtons(disabled: boolean): void {
    const prevBtn = document.getElementById('prevBtn') as HTMLButtonElement;
    const nextBtn = document.getElementById('nextBtn') as HTMLButtonElement;

    if (prevBtn) {
      prevBtn.disabled = disabled || this.currentPage === 1;
    }
    if (nextBtn) {
      nextBtn.disabled = disabled || this.currentPage === this.totalPages;
    }
  }

  /**
   * Load next page
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.loadBlogPosts(this.currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Load previous page
   */
  previousPage(): void {
    if (this.currentPage > 1) {
      this.loadBlogPosts(this.currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Navbar scroll effect
    window.addEventListener('scroll', () => {
      const navbar = document.getElementById('navbar');
      if (window.scrollY > 50) {
        navbar?.classList.add('scrolled');
      } else {
        navbar?.classList.remove('scrolled');
      }
    });
  }

  /**
   * Show loading indicator
   */
  private showLoading(message: string = 'Loading blog posts...'): void {
    const container = document.getElementById('blogPostsContainer');
    if (container) {
      container.innerHTML = `
        <div class="blog-loading">
          <div class="loading-spinner"></div>
          <p>${message}</p>
        </div>
      `;
    }
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    const container = document.getElementById('blogPostsContainer');
    if (container) {
      container.innerHTML = `
        <div class="blog-error">
          <p>${message}</p>
          <button class="btn-primary" onclick="window.blogListUI.loadBlogPosts()">
            Try Again
          </button>
        </div>
      `;
    }
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'blog-notification success';
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.add('show');
    }, 100);

    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
}

// Mobile menu toggle
function toggleMobileMenu(): void {
  const hamburger = document.querySelector('.hamburger');
  const mobileMenu = document.getElementById('mobileMenu');
  hamburger?.classList.toggle('active');
  mobileMenu?.classList.toggle('active');
}

// Initialize on page load
const blogListUI = new BlogListUI();
document.addEventListener('DOMContentLoaded', () => {
  blogListUI.initialize();
});

// Expose to window for HTML onclick handlers
declare global {
  interface Window {
    blogListUI: BlogListUI;
    toggleMobileMenu: () => void;
  }
}

window.blogListUI = blogListUI;
window.toggleMobileMenu = toggleMobileMenu;
